import React, { useState } from "react";
import { useVerification } from "../hooks/useVerification";
import Button from "./ui/Button";
import { 
    Shield, 
    CheckCircle, 
    XCircle, 
    Clock, 
    AlertTriangle,
    X,
    ArrowRight,
    FileText,
    Camera,
    MapPin,
    Mail,
    Phone,
    Building
} from "lucide-react";
import { useTranslation } from "react-i18next";

interface VerificationRequirementDialogProps {
    isOpen: boolean;
    onClose: () => void;
    title?: string;
    message?: string;
    showProgress?: boolean;
    showMissingSteps?: boolean;
    customAction?: {
        label: string;
        onClick: () => void;
    };
    variant?: "default" | "warning" | "error";
}

const VerificationRequirementDialog: React.FC<VerificationRequirementDialogProps> = ({
    isOpen,
    onClose,
    title,
    message,
    showProgress = true,
    showMissingSteps = true,
    customAction,
    variant = "default"
}) => {
    const { t } = useTranslation();
    const { getVerificationResult, goToVerification, getProgress } = useVerification();
    const [isClosing, setIsClosing] = useState(false);
    
    // Use translation defaults if not provided
    const dialogTitle = title || t("verification.required");
    const dialogMessage = message || t("verification.completeToAccess");

    if (!isOpen) return null;

    const result = getVerificationResult();
    const progress = getProgress();

    const handleClose = () => {
        setIsClosing(true);
        setTimeout(() => {
            setIsClosing(false);
            onClose();
        }, 200);
    };

    const handleVerifyNow = () => {
        goToVerification();
        handleClose();
    };

    const getVariantStyles = () => {
        switch (variant) {
            case "warning":
                return {
                    icon: AlertTriangle,
                    iconColor: "text-orange-600",
                    iconBg: "bg-orange-100",
                    borderColor: "border-orange-200",
                    bgColor: "bg-orange-50"
                };
            case "error":
                return {
                    icon: XCircle,
                    iconColor: "text-red-600",
                    iconBg: "bg-red-100",
                    borderColor: "border-red-200",
                    bgColor: "bg-red-50"
                };
            default:
                return {
                    icon: Shield,
                    iconColor: "text-blue-600",
                    iconBg: "bg-blue-100",
                    borderColor: "border-blue-200",
                    bgColor: "bg-blue-50"
                };
        }
    };

    const getStepIcon = (stepKey: string) => {
        switch (stepKey) {
            case "identity":
                return Camera;
            case "business":
                return Building;
            case "addressProof":
                return MapPin;
            case "email":
                return Mail;
            case "phone":
                return Phone;
            default:
                return FileText;
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case "verified":
                return CheckCircle;
            case "rejected":
                return XCircle;
            default:
                return Clock;
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case "verified":
                return "text-green-600";
            case "rejected":
                return "text-red-600";
            default:
                return "text-gray-500";
        }
    };

    const variantStyles = getVariantStyles();
    const IconComponent = variantStyles.icon;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 md:p-6">
            {/* Backdrop */}
            <div 
                className={`absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-200 ${
                    isClosing ? "opacity-0" : "opacity-100"
                }`}
                onClick={handleClose}
            />
            
            {/* Dialog */}
            <div className={`relative w-full max-w-sm sm:max-w-md md:max-w-lg lg:max-w-xl transform transition-all duration-200 ${
                isClosing ? "scale-95 opacity-0" : "scale-100 opacity-100"
            }`}>
                <div className={`${variantStyles.bgColor} ${variantStyles.borderColor} border rounded-xl sm:rounded-2xl shadow-2xl max-h-[90vh] sm:max-h-[85vh] md:max-h-[80vh] overflow-hidden`}>
                    {/* Header */}
                    <div className="flex items-start justify-between p-4 sm:p-5 md:p-6 pb-3 sm:pb-4">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                            <div className={`p-2 sm:p-3 rounded-lg ${variantStyles.iconBg} flex-shrink-0`}>
                                <IconComponent className={`h-5 w-5 sm:h-6 sm:w-6 ${variantStyles.iconColor}`} />
                            </div>
                            <div className="min-w-0 flex-1">
                                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 leading-tight">{dialogTitle}</h2>
                                <p className="text-xs sm:text-sm text-gray-600 mt-1">
                                    {t("verification.completeToUnlock")}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={handleClose}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white/50 rounded-lg transition-colors flex-shrink-0 ml-2"
                        >
                            <X className="h-4 w-4 sm:h-5 sm:w-5" />
                        </button>
                    </div>

                    {/* Content - Scrollable */}
                    <div className="px-4 sm:px-5 md:px-6 pb-4 sm:pb-5 md:pb-6 space-y-4 sm:space-y-5 md:space-y-6 overflow-y-auto max-h-[calc(90vh-120px)] sm:max-h-[calc(85vh-140px)] md:max-h-[calc(80vh-160px)]">
                        {/* Message */}
                        <div className="text-sm sm:text-base text-gray-700 leading-relaxed">
                            {dialogMessage}
                        </div>

                        {/* Progress Bar */}
                        {showProgress && (
                            <div className="space-y-2 sm:space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className="text-xs sm:text-sm font-medium text-gray-700">
                                        {t("verification.verificationProgress")}
                                    </span>
                                    <span className="text-xs sm:text-sm font-semibold text-gray-900">
                                        {progress}%
                                    </span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2 sm:h-3 overflow-hidden">
                                    <div 
                                        className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-500 ease-out"
                                        style={{ width: `${progress}%` }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Missing Steps */}
                        {showMissingSteps && result.missingSteps.length > 0 && (
                            <div className="space-y-2 sm:space-y-3">
                                <h3 className="text-xs sm:text-sm font-semibold text-gray-800">
                                    {t("verification.missingSteps")}
                                </h3>
                                <div className="space-y-2">
                                    {result.missingSteps.map((step, index) => {
                                        const StepIcon = getStepIcon(step.key);
                                        const StatusIcon = getStatusIcon(step.status);
                                        
                                        return (
                                            <div 
                                                key={index}
                                                className="flex items-start gap-2 sm:gap-3 p-2 sm:p-3 bg-white/70 rounded-lg border border-gray-200"
                                            >
                                                <div className="flex-shrink-0 mt-0.5">
                                                    <StepIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-xs sm:text-sm font-medium text-gray-900 truncate">
                                                            {step.title}
                                                        </span>
                                                        <StatusIcon className={`h-3 w-3 sm:h-4 sm:w-4 ${getStatusColor(step.status)} flex-shrink-0`} />
                                                    </div>
                                                    <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                                                        {step.status === "rejected" 
                                                            ? t("verification.documentRejected")
                                                            : t("verification.requiredForVerification")
                                                        }
                                                    </p>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Completed Steps (if any) */}
                        {result.verificationSteps.filter(step => step.status === "verified").length > 0 && (
                            <div className="space-y-2 sm:space-y-3">
                                <h3 className="text-xs sm:text-sm font-semibold text-gray-800">
                                    {t("verification.completedSteps")}
                                </h3>
                                <div className="space-y-2">
                                    {result.verificationSteps
                                        .filter(step => step.status === "verified")
                                        .map((step, index) => {
                                            const StepIcon = getStepIcon(step.key);
                                            
                                            return (
                                                <div 
                                                    key={index}
                                                    className="flex items-start gap-2 sm:gap-3 p-2 sm:p-3 bg-green-50 rounded-lg border border-green-200"
                                                >
                                                    <div className="flex-shrink-0 mt-0.5">
                                                        <StepIcon className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-xs sm:text-sm font-medium text-green-900 truncate">
                                                                {step.title}
                                                            </span>
                                                            <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 text-green-600 flex-shrink-0" />
                                                        </div>
                                                        <p className="text-xs text-green-700 mt-1">
                                                            {t("verification.successfullyVerified")}
                                                        </p>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                </div>
                            </div>
                        )}

                        {/* Actions */}
                        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-2 sticky bottom-0 bg-inherit">
                            {customAction ? (
                                <>
                                    <Button
                                        variant="outline"
                                        onClick={handleClose}
                                        className="flex-1 text-xs sm:text-sm py-2 sm:py-3"
                                    >
                                        {t("verification.cancel")}
                                    </Button>
                                    <Button
                                        onClick={customAction.onClick}
                                        className="flex-1 text-xs sm:text-sm py-2 sm:py-3"
                                    >
                                        {customAction.label}
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <Button
                                        variant="outline"
                                        onClick={handleClose}
                                        className="flex-1 text-xs sm:text-sm py-2 sm:py-3"
                                    >
                                        {t("verification.maybeLater")}
                                    </Button>
                                    <Button
                                        onClick={handleVerifyNow}
                                        className="flex-1 text-xs sm:text-sm py-2 sm:py-3"
                                    >
                                        <Shield className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                                        <span className="hidden xs:inline">{t("verification.verifyNow")}</span>
                                        <span className="xs:hidden">{t("verification.verify")}</span>
                                        <ArrowRight className="h-3 w-3 sm:h-4 sm:w-4 ml-1 sm:ml-2" />
                                    </Button>
                                </>
                            )}
                        </div>

                        {/* Additional Info */}
                        <div className="text-center pb-2">
                            <p className="text-xs text-gray-500 leading-relaxed">
                                {t("verification.verificationTime")}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VerificationRequirementDialog;
